import { useTranslation } from 'react-i18next'
import css from 'classnames'
import { observer } from 'mobx-react-lite'
import { NavLink } from 'react-router-dom'
import { MdOutlineConfirmationNumber } from 'react-icons/md'
import { Props } from './types'
import { ToolTip, UserInfo } from '~/shared/ui'
import { SubMenu } from '../sub-menu'
import { NavigationLink } from '../nav-link'
import { useStore } from '~/app/store'
import { hasUserPermission } from '~/shared/lib/permissions'

export const NavMenu = observer(({ items, isOpen }: Props) => {
  const { t } = useTranslation()
  const { t: tTickets } = useTranslation('tickets')

  const {
    usersStore: { userProfile },
    ticketsStore: { hasUnreadUpdates },
  } = useStore()

  const navIconClassName = ({ isActive }: { isActive: boolean }) =>
    css(
      'text-xl flex items-start justify-center relative group text-gray-900 p-3 rounded transition-all hover:bg-slate-50',
      {
        'bg-gray-100': isActive,
      },
    )

  const getMenuText = (localeTextName: string, fallbackName: string) => {
    const translatedText = t(localeTextName)
    return translatedText === localeTextName ? fallbackName : translatedText
  }

  const menu = items
    .filter(
      (item) =>
        !item.requiredPermission ||
        hasUserPermission(userProfile, item.requiredPermission),
    )
    .map((item) => ({
      ...item,
      subLinks: item.subLinks?.filter(
        (link) =>
          !link.requiredPermission ||
          hasUserPermission(userProfile, link.requiredPermission),
      ),
    }))
    .map((l) => {
      return !l?.subLinks ? (
        <li className="group relative " key={l.link}>
          <NavigationLink
            link={l.link}
            icon={l.icon || ''}
            text={getMenuText(l.localeTextName, l.name)}
            isOpen={isOpen}
            activeClass="bg-gray-50"
          />

          <div
            className={css(
              'w-[2.5px] h-4 rounded-md bg-blue-600 absolute left-0 top-4 opacity-0 group-hover:opacity-100 transition-all',
              {
                hidden: !isOpen,
              },
            )}
          />
        </li>
      ) : (
        <SubMenu key={l.link} item={l} isOpen={isOpen} />
      )
    })

  const ticketsLink = (
    <NavLink
      to="/tickets"
      className={navIconClassName}
      aria-label={
        hasUnreadUpdates
          ? tTickets('ticketsUnreadUpdates')
          : tTickets('tickets')
      }
    >
      <MdOutlineConfirmationNumber size={24} />
      {hasUnreadUpdates && (
        <span
          aria-hidden="true"
          className="absolute right-2 top-2 h-2 w-2 rounded-full bg-red-500 ring-2 ring-white"
        />
      )}
    </NavLink>
  )

  return (
    <ul className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden py-4 px-3 whitespace-pre text-black-700 flex flex-col justify-start">
      {menu}

      <div className="flex justify-between items-center py-2 mt-auto ">
        <div
          className={css('flex items-center w-full', {
            'gap-3': isOpen,
            'flex-col gap-2': !isOpen,
          })}
        >
          {!isOpen ? (
            <ToolTip
              tooltipText="Тикеты"
              wrapperClassName="block"
              minWidth="max-content"
              maxWidth="220px"
              placement="right"
            >
              {ticketsLink}
            </ToolTip>
          ) : (
            ticketsLink
          )}
          <NavigationLink
            link="/settings"
            icon="settings"
            text={t('settings') as string}
            isOpen={isOpen}
            activeClass="bg-gray-100"
            className={css({
              '[&>p]:hidden': isOpen,
              'justify-center [&>p]:hidden': !isOpen,
            })}
          />
          <UserInfo
            email={userProfile?.email}
            name={userProfile?.name}
            id={userProfile?.id}
            className={css('gap-3', {
              hidden: !isOpen,
            })}
          />
        </div>
      </div>
    </ul>
  )
})
